import { useForm } from '@inertiajs/react'
import { useFlashToast } from '../hooks/useFlashToast'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagnifyingGlass, faFileLines, faCircleCheck,
  faHourglassHalf, faSpinner, faCircleXmark, faArrowLeft
} from '@fortawesome/free-solid-svg-icons'

const STATUS_CONFIG = {
  menunggu: { label: 'Menunggu', icon: faHourglassHalf, bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
  diproses: { label: 'Sedang Diproses', icon: faSpinner, bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-200' },
  selesai:  { label: 'Selesai', icon: faCircleCheck, bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  ditolak:  { label: 'Ditolak', icon: faCircleXmark, bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' },
}

export default function ReportTracking({ tracking_code = '', result = null }) {
  useFlashToast()
  const { data, setData, post, processing } = useForm({ tracking_code: tracking_code || '' })

  const submit = (e) => {
    e.preventDefault()
    post('/cek-laporan')
  }

  const status = result?.found ? STATUS_CONFIG[result.status] : null

  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased">
      <Navbar />

      <main id="cek-laporan" className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 mb-6 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5" />
            Kembali ke Beranda
          </a>

          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faFileLines} className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Cek Status Laporan</h1>
            <p className="mt-2 text-slate-500 text-sm leading-relaxed">
              Masukkan kode pelacakan yang Anda terima saat mengirim laporan.
              Informasi yang ditampilkan terbatas demi kerahasiaan laporan.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <label htmlFor="tracking_code" className="block text-sm font-semibold text-slate-700 mb-2">
              Kode Pelacakan
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="tracking_code"
                type="text"
                value={data.tracking_code}
                onChange={(e) => setData('tracking_code', e.target.value.toUpperCase())}
                placeholder="WBS-2026-XXXXXXXX"
                className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="submit"
                disabled={processing}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold rounded-xl transition-colors"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} className="w-4 h-4" />
                {processing ? 'Mencari...' : 'Cek Status'}
              </button>
            </div>
          </form>

          {result && !result.found && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
              <FontAwesomeIcon icon={faCircleXmark} className="w-8 h-8 text-red-400 mb-2" />
              <p className="font-semibold text-red-800">Kode tidak ditemukan</p>
              <p className="text-sm text-red-600 mt-1">Pastikan kode pelacakan sudah benar. Kode bersifat case-insensitive.</p>
            </div>
          )}

          {result?.found && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">Kode Pelacakan</p>
                <p className="text-lg font-bold font-mono text-slate-900 tracking-wider">{result.tracking_code}</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Status</span>
                  {status && (
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ring-1 ${status.bg} ${status.text} ${status.ring}`}>
                      <FontAwesomeIcon icon={status.icon} className="w-3.5 h-3.5" />
                      {result.status_label}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Kategori</span>
                  <span className="text-sm font-medium text-slate-800 text-right">{result.category}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Tanggal Laporan</span>
                  <span className="text-sm font-medium text-slate-800">{result.created_at}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Terakhir Diperbarui</span>
                  <span className="text-sm font-medium text-slate-800">{result.updated_at}</span>
                </div>
              </div>
              <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
                <p className="text-xs text-blue-700 leading-relaxed">
                  Untuk detail lengkap dan komunikasi dengan admin, silakan{' '}
                  <a href="/login" className="font-semibold underline hover:text-blue-900">masuk ke akun</a>{' '}
                  Anda di dashboard pelapor.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}
